// @/src/core/store/inventario.store.ts

import { createStore } from '@/core/utils/store.utils';

// Importación de tipos
import type {
    InventarioState,
    ProductoInventario,
    ProductoProveedorInventario,
    Venta,
    EstadoPedidoProveedor,
} from '@/core/types';

// Importación de stores de los que depende
import { configuracionStore, type ConfigState } from './configuracion.store';
import { ventasStore, type VentasState } from './ventas.store';

/**
 * @file src/core/store/inventario.store.ts
 * @description Store para gestionar el estado calculado del inventario con sistema de señales inteligente.
 */

// --- Estado Inicial ---
const initialState: InventarioState = {
    inventario: [],
    productosProveedor: [],
    isLoading: false,
    error: null,
};

// --- Creación del Store ---
const inventarioStore = createStore<InventarioState>(initialState);

// --- Sistema de Señales Mejorado ---
let isInventoryCalculated = false; // Bandera para saber si ya se calculó al menos una vez
let needsRecalculation = false; // Bandera para indicar si necesita recálculo
let calculationInProgress = false; // Bandera para evitar cálculos simultáneos

/**
 * Marca el inventario como que necesita recálculo.
 * Se llama desde ventas.service.ts y configuracion.service.ts
 */
function markInventoryForUpdate(): void {
    console.log('[InventarioStore] 🔄 Inventario marcado para actualización');
    needsRecalculation = true;
}

/**
 * Verifica si el inventario necesita ser calculado o recalculado.
 */
function shouldCalculateInventory(): boolean {
    return !isInventoryCalculated || needsRecalculation;
}

/**
 * Resetea las banderas del sistema de señales (para logout)
 */
function resetSignals(): void {
    isInventoryCalculated = false;
    needsRecalculation = false;
    calculationInProgress = false;
}

// --- Lógica de Negocio y Cálculo ---

/**
 * Calcula el estado del inventario basado en la configuración de productos,
 * los ingresos y las ventas.
 * @param configState El estado del store de configuración.
 * @param ventasState El estado del store de ventas.
 * @returns El nuevo estado del inventario.
 */
interface InventarioCalculado {
    inventario: ProductoInventario[];
    productosProveedor: ProductoProveedorInventario[];
}

function resolverEstadoProveedor(venta: Venta): EstadoPedidoProveedor {
    if (venta.estadoPedidoProveedor) return venta.estadoPedidoProveedor;
    if (['Devolución', 'Devolución Entregada'].includes(venta.estadoPedido)) {
        return 'devuelto';
    }
    if (['Entregado', 'Liquidado'].includes(venta.estadoPedido)) return 'entregado';
    if (venta.estadoPedido.includes('En Camino')) return 'en_transito';
    return 'pendiente_compra';
}

function contarPedidos(
    ventas: Venta[],
    estadosProveedor: EstadoPedidoProveedor[]
): number {
    return ventas.filter((venta) =>
        estadosProveedor.includes(resolverEstadoProveedor(venta))
    ).length;
}

function calcularInventario(configState: ConfigState, ventasState: VentasState): InventarioCalculado {
    const { configProductosData, ingresosInventarioData } = configState;
    // Usar allVentas si está disponible (lista completa), sino fallback a ventas (paginada)
    // Esto corrige el bug de cálculo incompleto
    const ventas = ventasState.allVentas && ventasState.allVentas.length > 0 ? ventasState.allVentas : ventasState.ventas;

    if (!configProductosData || configProductosData.length === 0) {
        return { inventario: [], productosProveedor: [] };
    }

    const productosPropios = configProductosData.filter(
        (producto) => producto.modalidadAbastecimiento !== 'proveedor_directo'
    );
    const productosDirectos = configProductosData.filter(
        (producto) => producto.modalidadAbastecimiento === 'proveedor_directo'
    );

    const inventario = productosPropios.map(productoConfig => {
        const { id, nombreProducto, cantidadInicial, umbralAlerta } = productoConfig;

        // 1. Calcular ingresos totales
        const ingresosTotales = (ingresosInventarioData || [])
            .filter(ingreso => ingreso.productoId === id)
            .reduce((sum: number, ingreso) => sum + (ingreso.cantidadIngresada || 0), 0);
        
        const stockBase = (cantidadInicial || 0) + ingresosTotales;

        // 2. Calcular unidades vendidas (que restan del stock)
        const ventasDelProducto = (ventas || []).filter(v => v.productoId === id);
        
        const stockNetoVendido = ventasDelProducto
            .filter(v => ![
                "Cancelado",
                "Devolución Entregada"
            ].includes(v.estadoPedido))
            .reduce((sum, v) => sum + (v.unidadesConsumidas || 0), 0);

        const stockDisponible = stockBase - stockNetoVendido;

        // 3. Calcular unidades por estado
        const calcularUnidadesPorEstados = (estados: string[]): number => {
            return ventasDelProducto
                .filter(v => estados.includes(v.estadoPedido))
                .reduce((sum, v) => sum + (v.unidadesConsumidas || 0), 0);
        };
        
        // Calcular unidades por estado según la lógica de negocio:
        // - Procesado → EN PROCESO
        // - Recolectado, 1# En Camino, 2# En Camino, 3# En Camino → EN CAMINO
        // - Entregado, Liquidado → VENDIDO
        // - Devolución → DEVOLUCIONES
        // - Cancelado, Devolución Entregada → Se suman de vuelta a STOCK DISPONIBLE (ya manejado en stockNetoVendido)
        const unidadesEnProceso = calcularUnidadesPorEstados(["Procesado"]);
        const unidadesEnCamino = calcularUnidadesPorEstados(["Recolectado", "1# En Camino", "2# En Camino", "3# En Camino"]);
        const unidadesVendidas = calcularUnidadesPorEstados(["Entregado", "Liquidado"]);
        const unidadesEnDevolucion = calcularUnidadesPorEstados(["Devolución"]);

        // 4. Determinar estado del stock
        let estadoStock: 'ok' | 'bajo' | 'na' = 'na';
        if (umbralAlerta > 0) {
            estadoStock = stockDisponible <= umbralAlerta ? 'bajo' : 'ok';
        }

        return {
            id,
            nombreProducto,
            stockDisponible,
            unidadesEnProceso,
            unidadesEnCamino,
            unidadesVendidas,
            unidadesEnDevolucion,
            umbralAlerta: umbralAlerta || 0,
            estadoStock
        };
    });

    const productosProveedor = productosDirectos.map((producto): ProductoProveedorInventario => {
        const ventasDelProducto = (ventas || []).filter(
            (venta) =>
                venta.productoId === producto.id &&
                venta.modalidadAbastecimiento === 'proveedor_directo' &&
                venta.estadoPedidoProveedor !== 'cancelado'
        );
        const proveedor = producto.proveedorDirecto;

        return {
            id: producto.id,
            nombreProducto: producto.nombreProducto,
            proveedor: proveedor?.nombre || 'Proveedor sin registrar',
            skuProveedor: proveedor?.skuProveedor,
            enlaceProducto: proveedor?.enlaceProducto,
            estadoDisponibilidad: proveedor?.estadoDisponibilidad || 'sin_confirmar',
            ultimaVerificacion: proveedor?.ultimaVerificacion,
            pedidosEnProceso: contarPedidos(ventasDelProducto, [
                'pendiente_compra',
                'pedido_realizado',
                'en_preparacion',
            ]),
            pedidosEnCamino: contarPedidos(ventasDelProducto, ['en_transito']),
            pedidosEntregados: contarPedidos(ventasDelProducto, ['entregado']),
            pedidosEnDevolucion: contarPedidos(ventasDelProducto, ['devuelto']),
        };
    });

    return { inventario, productosProveedor };
}

/**
 * Calcula el inventario solo si es necesario según el sistema de señales.
 * @param configState El estado del store de configuración.
 * @param ventasState El estado del store de ventas.
 * @returns Una promesa que se resuelve cuando el cálculo termina.
 */
async function calculateInventoryIfNeeded(): Promise<void> {
    // Si ya hay un cálculo en progreso, esperar a que termine
    if (calculationInProgress) {
        console.log('[InventarioStore] ⏳ Cálculo en progreso, esperando...');
        return;
    }

    // Si los stores base están cargando, esperar a que terminen
    const configState = configuracionStore.getState();
    const ventasState = ventasStore.getState();

    if (configState.isLoading || ventasState.isLoading) {
        console.log('[InventarioStore] ⏳ Stores base están cargando, esperando...');
        inventarioStore.update(state => ({ ...state, isLoading: true }));
        
        // Esperar a que los stores terminen de cargar
        await waitForStoresReady();
        
        // Una vez que los stores estén listos, intentar calcular de nuevo
        return calculateInventoryIfNeeded();
    }

    // Si no necesita cálculo, no hacer nada
    if (!shouldCalculateInventory()) {
        console.log('[InventarioStore] ✅ Inventario ya calculado y actualizado');
        inventarioStore.update(state => ({ ...state, isLoading: false }));
        return;
    }

    // Marcar que el cálculo está en progreso
    calculationInProgress = true;

    try {
        console.log('[InventarioStore] 🔄 Calculando inventario...');
        inventarioStore.update(state => ({ ...state, isLoading: true }));
        
        // Obtener los datos más recientes después de la espera
        const freshConfigState = configuracionStore.getState();
        const freshVentasState = ventasStore.getState();
        const inventarioCalculado = calcularInventario(freshConfigState, freshVentasState);
        
        inventarioStore.update(state => ({
            ...state,
            inventario: inventarioCalculado.inventario,
            productosProveedor: inventarioCalculado.productosProveedor,
            isLoading: false,
            error: null
        }));

        // Marcar como calculado y limpiar bandera de recálculo
        isInventoryCalculated = true;
        needsRecalculation = false;
        
        console.log(
            `[InventarioStore] ✅ Inventario calculado: ${inventarioCalculado.inventario.length} propios, ${inventarioCalculado.productosProveedor.length} enviados por proveedor`
        );
    } catch (error) {
        console.error("[InventarioStore] Error al calcular el inventario:", error);
        inventarioStore.update(state => ({ 
            ...state, 
            isLoading: false, 
            error: 'Error al calcular inventario' 
        }));
        throw error;
    } finally {
        calculationInProgress = false;
    }
}

/**
 * Espera a que los stores base estén listos (no loading).
 * @param configState Estado actual de configuración
 * @param ventasState Estado actual de ventas
 */
async function waitForStoresReady(): Promise<void> {
    return new Promise((resolve) => {
        const checkStores = () => {
            const configState = configuracionStore.getState();
            const ventasState = ventasStore.getState();
            if (!configState.isLoading && !ventasState.isLoading) {
                resolve();
            } else {
                // Revisar cada 100ms hasta que estén listos
                setTimeout(checkStores, 100);
            }
        };
        checkStores();
    });
}

/**
 * Fuerza el recálculo del inventario independientemente del sistema de señales.
 * Útil para casos excepcionales.
 */
async function forceRecalculate(): Promise<void> {
    needsRecalculation = true;
    return calculateInventoryIfNeeded();
}

/**
 * Detiene las suscripciones y resetea solo las banderas, NO los datos.
 * Los datos del inventario deben persistir durante la navegación.
 */
function destroy() {
    console.log('[InventarioStore] 🧹 Limpiando banderas, conservando datos calculados');
    resetSignals();
    // NO resetear el estado completo - los datos deben persistir
    // inventarioStore.update(() => initialState);
}

// Exportación del store público
export const publicInventarioStore = {
    subscribe: inventarioStore.subscribe,
    getState: inventarioStore.getState,
    recalculateAndStore: calculateInventoryIfNeeded,
    forceRecalculate: forceRecalculate,
    setLoading: (isLoading: boolean) => inventarioStore.update(state => ({...state, isLoading})),
    markForUpdate: markInventoryForUpdate,
    resetSignals,
    destroy
};
